import Vue from 'vue'
import _ from 'lodash'
import { router } from '~router/router'
import DynamicCacheControl from '~services/DynamicCacheControl'
import Store from '~store/store'

const auth = new Vue({
  data: function () {
    return {
      authToken: '',
      organizations: [],
      currentOrganization: {},
      loggedUser: {}
    }
  }
})

const userCache = () => {
  return DynamicCacheControl.getInstance('user')
}

const loadUserCache = () => {
  return new Promise(resolve => {
    const getToken = userCache()
      .getItem('authToken')
      .then(authToken => {
        auth.$data.authToken = authToken
      })
    const getOrganization = userCache()
      .getItem('organizations')
      .then(organizations => {
        auth.$data.organizations = organizations
      })
    const getCurrentOrganization = userCache()
      .getItem('currentOrganization')
      .then(currentOrganization => {
        auth.$data.currentOrganization = currentOrganization
      })
    const getLoggedUser = userCache()
      .getItem('loggedUser')
      .then(loggedUser => {
        auth.$data.loggedUser = loggedUser
      })

    Promise.all([getToken, getOrganization, getCurrentOrganization, getLoggedUser])
      .then(() => {
        resolve(auth.$data.authToken)
      })
      .catch(() => {
        resolve()
      })

    // to prevent problems with localforage, sometimes doesnt respond.
    setTimeout(() => {
      resolve()
    }, 3000)
  })
}

const setToken = authToken => {
  auth.$data.authToken = authToken
  localStorage.setItem('token', authToken)
  userCache().setItem('authToken', authToken)
}

const setOrganizations = organizations => {
  if (!organizations) return
  auth.$data.organizations = organizations
  userCache().setItem('organizations', organizations)
}

const setCurrentOrganization = organization => {
  if (!organization) return
  organization.ui_customizations = organization.ui_customizations || {}
  auth.$data.currentOrganization = organization
  userCache().setItem('currentOrganization', organization)
}

const setLoggedUser = loggedUser => {
  if (!loggedUser) return
  auth.$data.loggedUser = loggedUser
  userCache().setItem('loggedUser', loggedUser)
}

export const logout = () => {
  Store.dispatch('setInitialDataLoaded', false)
  localStorage.setItem('token', '')
  userCache().clear()
  auth.$data.authToken = ''
  auth.$data.loggedUser = {}
  auth.$data.currentOrganization = {}
  auth.$data.organizations = []
  router.push({ name: 'login' })
}

const hasRoleInCurrentOrganization = roles => {
  const loggedUser = auth.$data.loggedUser
  return _.intersection(loggedUser.roles, _.isArray(roles) ? roles : [roles]).length > 0
}

const areFeatureEnabled = flag => {
  return true
  // const project = Store.state.projectsModule.selectedProject
  // if (!project || !project.properties) return false
  // const property = project.properties[flag]
  // return Boolean(property) !== false
}

/**
 * Verifica se o usuário tem acesso a uma permissão específica.
 *
 * @param {string} permissionKey - Chave da permissão (ex: 'project.view', 'org.users.manage')
 * @param {string|null} projectId - ID do projeto (opcional, para permissões de projeto)
 * @returns {boolean} - true se tem acesso, false caso contrário
 */
const hasAccess = (permissionKey, projectId = null) => {
  const loggedUser = auth.$data.loggedUser

  // Staff e Admin têm acesso total
  if (hasRoleInCurrentOrganization(['staff', 'admin'])) {
    return true
  }

  // Se não tem permissões carregadas, bloqueia
  if (!loggedUser.permissions) {
    return false
  }

  // Permissão de projeto
  if (permissionKey.startsWith('project.')) {
    if (!projectId) {
      // Se não passou projectId, verifica se tem em algum projeto
      const projectPermissions = loggedUser.permissions.projects || {}
      return Object.values(projectPermissions).some(perms => perms.includes(permissionKey))
    }
    // Verifica no projeto específico
    const projectPerms = loggedUser.permissions.projects?.[projectId] || []
    return projectPerms.includes(permissionKey)
  }

  // Permissão de organização
  const orgPerms = loggedUser.permissions.org || []
  return orgPerms.includes(permissionKey)
}

/**
 * Verifica se o usuário tem acesso a pelo menos uma das permissões.
 *
 * @param {string[]} permissionKeys - Array de chaves de permissão
 * @param {string|null} projectId - ID do projeto (opcional)
 * @returns {boolean} - true se tem acesso a pelo menos uma permissão
 */
const hasAnyAccess = (permissionKeys, projectId = null) => {
  if (hasRoleInCurrentOrganization(['staff', 'admin'])) {
    return true
  }
  return permissionKeys.some(key => hasAccess(key, projectId))
}

/**
 * Verifica se o usuário tem acesso a todas as permissões.
 *
 * @param {string[]} permissionKeys - Array de chaves de permissão
 * @param {string|null} projectId - ID do projeto (opcional)
 * @returns {boolean} - true se tem acesso a todas as permissões
 */
const hasAllAccess = (permissionKeys, projectId = null) => {
  if (hasRoleInCurrentOrganization(['staff', 'admin'])) {
    return true
  }
  return permissionKeys.every(key => hasAccess(key, projectId))
}
const roleAdminAndUp = () => {
  return hasRoleInCurrentOrganization(['staff', 'admin'])
}

const roleStaff = () => {
  const currentOrganization = auth.$data.currentOrganization || {}
  return hasRoleInCurrentOrganization(['staff', 'admin']) && currentOrganization.id === 0
}

const getOrganizationPreference = (uiResource, value) => {
  const currentOrganization = auth.$data.currentOrganization || {}
  if (!currentOrganization.ui_customizations) return false
  const uiCustomization = currentOrganization.ui_customizations[uiResource]
  return uiCustomization === value
}

const getUserPreference = preferenceName => {
  const loggedUser = auth.$data.loggedUser || {}
  if (!loggedUser.preferences) return undefined
  return loggedUser.preferences[preferenceName]
}

const getUICustomization = uiResource => {
  const currentOrganization = auth.$data.currentOrganization || {}
  currentOrganization.ui_customizations = currentOrganization.ui_customizations || {}
  return currentOrganization.ui_customizations[uiResource]
}

export default {
  install(Vue) {
    Vue.prototype.$setCurrentOrganization = Vue.setCurrentOrganization = setCurrentOrganization
    Vue.prototype.$setOrganizations = Vue.setOrganizations = setOrganizations
    Vue.prototype.$setLoggedUser = Vue.setLoggedUser = setLoggedUser
    Vue.prototype.$setToken = Vue.setToken = setToken
    Vue.prototype.$logout = Vue.logout = logout
    Vue.prototype.$loadUserCache = Vue.loadUserCache = loadUserCache

    // Reactive Getters
    Object.defineProperty(Vue.prototype, '$authToken', {
      get: () => {
        return auth.$data.authToken
      }
    })

    Object.defineProperty(Vue, 'authToken', {
      get: () => {
        return auth.$data.authToken
      }
    })

    window.__getAuthToken__ = () => {
      return auth.$data.authToken
    }

    Object.defineProperty(Vue.prototype, '$organizations', {
      get: () => {
        return auth.$data.organizations
      }
    })

    Object.defineProperty(Vue, 'organizations', {
      get: () => {
        return auth.$data.organizations
      }
    })

    Object.defineProperty(Vue.prototype, '$isGlobalOrg', {
      get: () => {
        return auth.$data.currentOrganization.id === 0
      }
    })

    Object.defineProperty(Vue, 'isGlobalOrg', {
      get: () => {
        return auth.$data.currentOrganization.id === 0
      }
    })

    Object.defineProperty(Vue.prototype, '$currentOrganization', {
      get: () => {
        return auth.$data.currentOrganization
      }
    })

    Object.defineProperty(Vue, 'currentOrganization', {
      get: () => {
        return auth.$data.currentOrganization
      }
    })

    Object.defineProperty(Vue.prototype, '$loggedUser', {
      get: () => {
        return auth.$data.loggedUser
      }
    })

    Object.defineProperty(Vue, 'loggedUser', {
      get: () => {
        return auth.$data.loggedUser
      }
    })

    Object.defineProperty(Vue.prototype, '$getOrganizationPreference', {
      get: () => {
        return getOrganizationPreference
      }
    })

    Object.defineProperty(Vue, 'getOrganizationPreference', {
      get: () => {
        return getOrganizationPreference
      }
    })

    Object.defineProperty(Vue.prototype, '$getUserPreference', {
      get: () => {
        return getUserPreference
      }
    })

    Object.defineProperty(Vue, 'getUserPreference', {
      get: () => {
        return getUserPreference
      }
    })

    Object.defineProperty(Vue.prototype, '$getUICustomization', {
      get: () => {
        return getUICustomization
      }
    })

    Object.defineProperty(Vue, 'getUICustomization', {
      get: () => {
        return getUICustomization
      }
    })

    Object.defineProperty(Vue.prototype, '$hasRoleInCurrentOrganization', {
      get: () => {
        return hasRoleInCurrentOrganization
      }
    })

    Object.defineProperty(Vue, 'hasRoleInCurrentOrganization', {
      get: () => {
        return hasRoleInCurrentOrganization
      }
    })

    Object.defineProperty(Vue.prototype, '$hasAccess', {
      get: () => {
        return hasAccess
      }
    })

    Object.defineProperty(Vue, 'hasAccess', {
      get: () => {
        return hasAccess
      }
    })

    Object.defineProperty(Vue.prototype, '$hasAnyAccess', {
      get: () => {
        return hasAnyAccess
      }
    })

    Object.defineProperty(Vue, 'hasAnyAccess', {
      get: () => {
        return hasAnyAccess
      }
    })

    Object.defineProperty(Vue.prototype, '$hasAllAccess', {
      get: () => {
        return hasAllAccess
      }
    })

    Object.defineProperty(Vue, 'hasAllAccess', {
      get: () => {
        return hasAllAccess
      }
    })

    Object.defineProperty(Vue.prototype, '$areFeatureEnabled', {
      get: () => {
        return areFeatureEnabled
      }
    })

    Object.defineProperty(Vue, 'areFeatureEnabled', {
      get: () => {
        return areFeatureEnabled
      }
    })

    Object.defineProperty(Vue.prototype, '$roleAdminAndUp', {
      get: () => {
        return roleAdminAndUp()
      }
    })

    Object.defineProperty(Vue.prototype, '$roleStaff', {
      get: () => {
        return roleStaff()
      }
    })

    Object.defineProperty(Vue, 'roleAdminAndUp', {
      get: () => {
        return roleAdminAndUp()
      }
    })

    Object.defineProperty(Vue, 'roleStaff', {
      get: () => {
        return roleStaff()
      }
    })
  }
}
